import {AnalysisGraphQL, GetAnalysisDataFrame, isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import {getTopLevelTableLineageNodes, StepDataFrameLineageNode} from 'analysis-tool/data/DatasetLineageUtils';
import {INITIAL_DOCUMENT_LAYOUT_CONFIGURATION} from 'analysis-tool/outputs/ConfigureDocumentUtil';
import {AnalysisOutput, OutputDocumentLayoutConfiguration} from 'analysis-tool/outputs/Types';
import {assert, assertAndContinue} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {MakeFieldsRequired} from 'util/Types';

import {CreateAnalysisTemplateDataFrameVariableParams} from './AnalysisTemplateDAO';

/**
 * @package
 */
export enum InputTableType {
  STATIC = 'STATIC',
  VARIABLE = 'VARIABLE',
}

export type InputTableLineageNode = MakeFieldsRequired<StepDataFrameLineageNode, 'data'>;

/**
 * An input table. Used when configuring an analysis template.
 * @package
 */
export interface InputTableConfigState {
  included: boolean;
  inputType: InputTableType;
  variable: DataFrameVariable | null;
  lineageNode: InputTableLineageNode;
  description: string;
}

/**
 * An input table. Use when applying an analysis template within the analysis tool.
 *
 * See `AnalysisTemplateVariableInputTable` for a narrower interface for external consumptions, which only
 * exposes persisted variable tables.
 */
export interface InputTableApplyState {
  included: boolean;
  inputType: InputTableType;
  dataFrameColumns: Array<DataFrameColumnDef> | null;
  templateLineageNode: InputTableLineageNode | null;
  variable: DataFrameVariable | null;
  lineageNode: InputTableLineageNode | null;
  description: string;
  columnMapping: {[templateColumn: string]: string};
}

/** @package */
export interface AnalysisTemplateConfigState {
  inputTables: Array<InputTableConfigState>;
  name: string;
  templateCollectionId: string | null;
  selectedOutputs: Array<AnalysisOutput>;
  outputDocumentLayoutConfiguration: OutputDocumentLayoutConfiguration;
}

/** @package */
export function getDefaultTemplateConfigStateForAnalysis(
  analysis: AnalysisGraphQL
): AnalysisTemplateConfigState {
  return {
    name: '',
    inputTables: getRootTablesFromPipeline(analysis, [], InputTableType.VARIABLE),
    templateCollectionId: null,
    selectedOutputs: [],
    outputDocumentLayoutConfiguration: INITIAL_DOCUMENT_LAYOUT_CONFIGURATION,
  };
}

interface DataFrameVariable {
  id: string;
  name: string;
  description: string;
  replaceStepId: string;
  replaceStepDataId: string;
  replaceStepDataIndex: number;
  replaceDataFrameColumnsSpec: Array<DataFrameColumnDef>;
}

/** @package */
export function getTemplateDataForLineageNode(
  lineageNode: InputTableLineageNode
): Omit<CreateAnalysisTemplateDataFrameVariableParams, 'description'> {
  let replaceStepDataId: string | null = null;
  let replaceStepDataIndex: number | null = null;

  for (const stepData of lineageNode.step.outputStepDatas) {
    for (let i = 0; i < stepData.dataFrames.length; i++) {
      if (stepData.dataFrames[i].id === lineageNode.data.dataFrame.id) {
        replaceStepDataId = stepData.id;
        replaceStepDataIndex = i;
        break;
      }
    }
  }

  assert(
    replaceStepDataId != null && replaceStepDataIndex != null,
    "Could not find lineage node's data frame in its step's output data frames."
  );

  return {
    name: lineageNode.data.dataFrame.name,
    replaceStepId: lineageNode.step.id,
    replaceStepDataId,
    replaceStepDataIndex,
    replaceDataFrameColumnsSpec: lineageNode.data.dataFrame.allColumnsJson,
  };
}

/**
 * Get the root tables of an analysis, which correspond to the VARIABLE and STATIC input tables of an analysis
 * template.
 *
 * @param pipeline The pipeline to turn into an analysis template OR the pipeline backing an analysis template
 * @param dataFrameVariables If the pipeline is backing an analysis template, the template's data frame
 *                           variables. Each `DataFrameVariable` corresponds to a root table in `pipeline`.
 * @param defaultInputType If the pipeline is to be turned into a pipeline, whether input tables should
 *                         default to VARIABLE or STATIC.
 */
export function getRootTablesFromPipeline(
  pipeline: PipelineGraphQL,
  dataFrameVariables: Array<DataFrameVariable>,
  defaultInputType: InputTableType = InputTableType.STATIC
): Array<InputTableConfigState> {
  const topLevelTableLineageNodes = getTopLevelTableLineageNodes(pipeline);

  return topLevelTableLineageNodes
    .filter((rootNode) => isAnalysisInputStepType(rootNode.step.definition.stepType) && rootNode.data != null)
    .map((rootNode) => {
      const lineageNode = rootNode as InputTableLineageNode;
      const templateData = getTemplateDataForLineageNode(lineageNode);
      const dataFrameVariable = dataFrameVariables.find(
        (dfv) =>
          dfv.replaceStepDataId === templateData.replaceStepDataId &&
          dfv.replaceStepDataIndex === templateData.replaceStepDataIndex &&
          dfv.replaceStepId === templateData.replaceStepId
      );
      return {
        included: true,
        inputType: dataFrameVariable ? InputTableType.VARIABLE : defaultInputType,
        variable: dataFrameVariable ?? null,
        lineageNode,
        description: dataFrameVariable ? dataFrameVariable.description : '',
      };
    });
}

/**
 * Get the `InputTableApplyState`s for an analysis template to apply it to an analysis.
 *
 * Input table apply states are a super set of `getRootTablesFromPipeline`. All root tables are included and we
 * give them a different input type based on wether there's a corresponding data frame variable to that root table
 * or not. Additionally, we add extra input tables for data frame variables that don't have a corresponding root table,
 * which happens when a template exists without raw datas and with data frame variables' replaceDataFrameColumnsSpec defined..
 *
 * When a root table does exist for an input table apply state, `templateLineageNode` is defined to point to that table, and
 * we can use that to build column mapping and preview UIs. Otherwise, only `dataFrameColumns` is defined, which still allows
 * use to build column mapping UIs but not preview UIs.
 *
 * @param pipeline The pipeline to turn into an analysis template OR the pipeline backing an analysis template
 * @param dataFrameVariables If the pipeline is backing an analysis template, the template's data frame
 *                           variables. Each `DataFrameVariable` corresponds to a root table in `pipeline`.
 * @param defaultInputType If the pipeline is to be turned into a pipeline, whether input tables should
 *                         default to VARIABLE or STATIC.
 */
export function getInputTableApplyStates(
  pipeline: PipelineGraphQL,
  dataFrameVariables: Array<DataFrameVariable>,
  defaultInputType: InputTableType = InputTableType.STATIC
): Array<InputTableApplyState> {
  const rootTables = getRootTablesFromPipeline(pipeline, dataFrameVariables, defaultInputType);

  const rootTableApplyStates: Array<InputTableApplyState> = rootTables.map((inputTable) => ({
    ...inputTable,
    templateLineageNode: inputTable.lineageNode,
    dataFrameColumns: inputTable.lineageNode.data.dataFrame.allColumnsJson,
    lineageNode: null,
    columnMapping: {},
  }));

  const dataFrameVariablesWithoutRootTable = dataFrameVariables.filter(
    (dataFrameVariable) =>
      !rootTables.some(
        ({variable: otherDataFrameVariable}) => dataFrameVariable.id === otherDataFrameVariable?.id
      )
  );

  const nonRootTableApplyStates: Array<InputTableApplyState> = dataFrameVariablesWithoutRootTable.map(
    (dataFrameVariable) => ({
      included: true,
      inputType: InputTableType.VARIABLE,
      variable: dataFrameVariable,
      templateLineageNode: null,
      dataFrameColumns: dataFrameVariable.replaceDataFrameColumnsSpec,
      lineageNode: null,
      columnMapping: {},
      description: dataFrameVariable ? dataFrameVariable.description : '',
    })
  );

  return [...nonRootTableApplyStates, ...rootTableApplyStates];
}

/**
 * A variable input table to an analysis template.
 */
export interface AnalysisTemplateVariableInputTable {
  /**
   * The input table variable.
   *
   * • `variable.description` is the user-facing description of the input table.
   * • `variable.id` identifies the input table.
   * • `variable.name` is the title of the analysis template input table. Currently, this is copied from and
   *                   always equal to `dataFrame.name`, but may be configurable in the future.
   */
  variable: Pick<DataFrameVariable, 'id' | 'name' | 'description'>;
  /**
   * The data frame backing the analysis template input table, which is copied from the prototype analysis.
   *
   * • `dataFrame.allColumnsJson` defines the expected columns:
   *     • `dataFrame.allColumnsJson[i].systemName` identifies the column in column mappings.
   *     • `dataFrame.allColumnsJson[i].type` is the expected column type. This is loosely enforced, as a
   *        template may still succeed with columns of different types.
   * • `dataFrame.id` is not used to identify the input table (see `variable.id`), but can be used to preview
   *                  the input table (including its row contents) from the prototype analysis.
   * • `dataFrame.name` is the title of the table in the prototype analysis.
   */
  dataFrame: GetAnalysisDataFrame;
}

/**
 * Given a InputTableApplyState, return its display name.
 *
 * @param inputTableApplyState To get the name from
 * @returns If `templateLineageNode` is defined, we return the name from the backing data frame. Otherwise, we return the name
 * stored on the variable.
 */
export function getInputTableApplyStateName(inputTableApplyState: InputTableApplyState): string {
  const maybeName =
    inputTableApplyState.templateLineageNode?.data.dataFrame.name ?? inputTableApplyState.variable?.name;
  assertAndContinue(maybeName != null, 'Expected either variable or templateLineageNode to be non-null');
  return maybeName ?? 'Table';
}
